import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WELCOME = {
  role: "assistant",
  text: "Hello! I'm the Prata Technologies AI assistant. Ask me anything about our AI, automation, WhatsApp and digital solutions — and if you'd like a tailored proposal, I can take your details right here.",
};

const getSessionId = () => {
  let id = localStorage.getItem("prata_chat_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("prata_chat_session", id);
  }
  return id;
};

const TypingIndicator = () => (
  <div
    data-testid="chat-typing-indicator"
    className="mr-auto flex max-w-[85%] items-center gap-1.5 rounded-2xl rounded-bl-sm border border-slate-200 bg-white px-4 py-3.5"
  >
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400"
        style={{ animationDelay: `${i * 0.15}s` }}
      />
    ))}
  </div>
);

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const sessionId = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    if (!sessionId.current) sessionId.current = getSessionId();
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/chat`, {
        message: text,
        session_id: sessionId.current,
      });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          text: "Sorry, I'm having trouble connecting right now. Please try again in a moment, or reach us through the contact form below.",
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-window"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 z-50 flex h-[min(560px,calc(100dvh-8rem))] w-[min(380px,calc(100vw-2rem))] flex-col overflow-hidden rounded-2xl border border-white/10 bg-white shadow-[0_24px_80px_-12px_rgba(5,10,31,0.45)] sm:right-6"
            role="dialog"
            aria-label="Prata Technologies AI assistant chat"
          >
            <div className="flex items-center gap-3 bg-navy-950 px-5 py-4">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/10 font-display text-base font-semibold text-electric-bright ring-1 ring-white/10">
                P
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-display text-sm font-medium text-white">
                  Prata AI Assistant
                </p>
                <p className="flex items-center gap-1.5 text-[11px] text-slate-400">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  Online · typically replies instantly
                </p>
              </div>
              <button
                data-testid="chat-close-button"
                aria-label="Close chat"
                onClick={() => setOpen(false)}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors duration-300 hover:bg-white/10 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div
              ref={scrollRef}
              data-lenis-prevent
              data-testid="chat-messages"
              className="flex-1 space-y-3 overflow-y-auto bg-slate-50 px-4 py-4"
            >
              {messages.map((msg, i) => (
                <div
                  key={i}
                  data-testid={`chat-message-${msg.role}-${i}`}
                  className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                    msg.role === "user"
                      ? "ml-auto rounded-br-sm bg-electric text-white"
                      : "mr-auto rounded-bl-sm border border-slate-200 bg-white text-slate-700"
                  }`}
                >
                  {msg.text}
                </div>
              ))}
              {sending && <TypingIndicator />}
            </div>

            <div className="border-t border-slate-200 bg-white px-3 py-3">
              <div className="flex items-center gap-2">
                <input
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="Type your message..."
                  aria-label="Type your message"
                  className="flex-1 rounded-full border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-[border-color,box-shadow] duration-300 focus:border-electric focus:ring-4 focus:ring-electric/10"
                />
                <button
                  data-testid="chat-send-button"
                  aria-label="Send message"
                  onClick={send}
                  disabled={sending || !input.trim()}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-electric text-white transition-[background-color,transform,opacity] duration-300 hover:scale-105 hover:bg-electric-bright active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
              <p className="mt-2 text-center text-[10px] text-slate-400">
                Powered by Prata Technologies AI
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        data-testid="chat-fab-button"
        aria-label={open ? "Close chat" : "Chat with our AI assistant"}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.2, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="fixed bottom-5 right-4 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-electric text-white shadow-[0_8px_30px_rgba(37,99,235,0.5)] transition-[background-color,transform] duration-300 hover:scale-105 hover:bg-electric-bright active:scale-95 sm:right-6"
      >
        {!open && (
          <span className="absolute -right-0.5 -top-0.5 flex h-3.5 w-3.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-cyan-400 opacity-75" />
            <span className="relative inline-flex h-3.5 w-3.5 rounded-full border-2 border-navy-950 bg-cyan-400" />
          </span>
        )}
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </motion.button>
    </>
  );
};

export default ChatWidget;
